<template>
    <CardContentPanel icon="warning" v-bind="{action, index}">
        <template #title="{freelancerName}">
            {{ $t('projectclient.projectview.activity.pending.actions.APPROVE_CONTRACT.title', {0: freelancerName}) }}
        </template>
        <template #body>
            <p>{{ $t('projectclient.projectview.activity.pending.actions.APPROVE_CONTRACT.description') }}</p>
            <p v-if="action.extraData.changes" class="contract-changes" v-html="action.extraData.changes"></p>
        </template>
        <template #actions>
            <NuxtLink
                external
                :to="`/proposal/pdf/${action.extraData.amendmentQuoteId}/summary`"
                target="_blank"
                data-testid="pending-action-card-cta-secondary"
            >
                <VJoyButton variant="ghost" size="small">
                    {{ $t('projectclient.projectview.activity.pending.actions.APPROVE_CONTRACT.action.secondary') }}
                </VJoyButton>
            </NuxtLink>
            <VJoyButton
                v-if="isParticipant"
                :loading="acceptingContractAmendment"
                variant="main"
                size="small"
                data-testid="pending-action-card-cta-primary"
                @click="approveContractChange"
            >
                {{ $t('projectclient.projectview.activity.pending.actions.APPROVE_CONTRACT.action.primary') }}
            </VJoyButton>
            <VJoyButton
                v-else-if="!isParticipant && hasAuthorityToApproveAmendment"
                variant="main"
                size="small"
                data-testid="pending-action-card-cta-primary"
                @click="handleShowDialog"
            >
                {{ $t('projectclient.projectview.activity.pending.actions.APPROVE_CONTRACT.action.primary') }}
            </VJoyButton>
        </template>
    </CardContentPanel>
    <ClientOpsConfirmationModal
        v-if="isClientOpsDialogVisible"
        v-model="nonParticipantValidationComment"
        :header="t('projectclient.projectview.non.participant.general.dialog.header')"
        :message="t('projectclient.projectview.non.participant.amendment.dialog.body')"
        :is-updating="acceptingContractAmendment"
        @confirm:action="approveContractChange"
        @close:dialog="handleShowDialog"
    />
</template>

<script setup lang="ts">
    import {computed} from 'vue';
    import {useClientOpsModal} from '@common/composables';
    import {ClientOpsConfirmationModal} from '@common/ui';
    import {storeToRefs} from 'pinia';
    import {useTranslation} from '#imports';
    import {VJoyButton} from '@maltjoy/core-vue';
    import CardContentPanel from '../CardContentPanel.vue';
    import {useProjectInformationStore, useUserStore} from '@/common/stores';
    import type {ActivityPendingActionByType} from '@/features/project-view';

    const {t} = useTranslation();
    const userStore = useUserStore();
    const {roles} = storeToRefs(userStore);

    const props = defineProps<{
        action: ActivityPendingActionByType<'APPROVE_CONTRACT'>;
        index: number;
    }>();

    const projectInformationStore = useProjectInformationStore();
    const {projectInformation, acceptingContractAmendment} = storeToRefs(projectInformationStore);

    // handle admin validation + clientOpsModal
    const isParticipant = computed(() => projectInformation.value?.participant);
    const hasAuthorityToApproveAmendment = computed(() => roles.value.includes('ROLE_COMPLETED_MISSION_EDITION'));

    const {nonParticipantValidationComment, isClientOpsDialogVisible, handleShowDialog} = useClientOpsModal();

    async function approveContractChange() {
        await projectInformationStore.acceptContractAmendment({
            projectId: projectInformationStore.projectInformation.projectId,
            quoteId: props.action.extraData.amendmentQuoteId,
            comment: nonParticipantValidationComment.value,
        });
    }
</script>

<style lang="scss" scoped>
    .contract-changes {
        white-space: pre-wrap;
    }
</style>
